import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { createScenario, deleteScenario, resetScenario, createScenarioFromBenchmarks, updateScenario } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Plus, Copy, Trash2, RotateCcw, Layers, Sparkles, ChevronDown, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function ScenarioBar({ scenarios, activeId, onSelect, onChange }) {
  const active = scenarios.find(s => s.id === activeId);
  const [openCreate, setOpenCreate] = useState(false);
  const [openDup, setOpenDup] = useState(false);
  const [dupForm, setDupForm] = useState({ name: "", description: "" });
  const [form, setForm] = useState({ name: "", description: "", clone_from: "" });
  const [saving, setSaving] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [openRename, setOpenRename] = useState(false);
  const [renameForm, setRenameForm] = useState({ name: "", description: "" });

  const openRenameDialog = () => {
    if (!active) return;
    setRenameForm({ name: active.name, description: active.description || "" });
    setOpenRename(true);
  };

  const submitRename = async () => {
    if (!active) return;
    const trimmed = renameForm.name.trim();
    if (!trimmed) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await updateScenario(active.id, { name: trimmed, description: renameForm.description.trim() });
      toast.success("Scenario renamed");
      setOpenRename(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally { setSaving(false); }
  };

  const runAiPricingScenario = async (tier) => {
    setAiRunning(true);
    const t = tier[0].toUpperCase() + tier.slice(1);
    const sourceName = active?.name || "current";
    toast.info(`Building "Market Pricing (${t})" from "${sourceName}" — inheriting your excluded items and edits…`, { duration: 6000 });
    try {
      const res = await createScenarioFromBenchmarks({
        name: "Market Pricing",
        tier,
        auto_analyze: true,
        clone_from: active?.id,
      });
      const parts = [`${res.applied} rates applied`];
      if (res.excluded_carried) parts.push(`${res.excluded_carried} kept excluded`);
      if (res.kept_original) parts.push(`${res.kept_original} kept original`);
      toast.success(`"${res.name}" created — ${parts.join(", ")}`);
      onChange();
      onSelect(res.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.slice(0, 100) || "AI pricing scenario failed");
    } finally {
      setAiRunning(false);
    }
  };

  const handleCreate = async (clone = false) => {
    if (!form.name.trim()) { toast.error("Please provide a scenario name"); return; }
    setSaving(true);
    try {
      const body = { name: form.name.trim(), description: form.description.trim() };
      if (clone) body.clone_from = form.clone_from || activeId;
      const res = await createScenario(body);
      toast.success(`Scenario "${res.name}" created`);
      onChange();
      onSelect(res.id);
      setOpenCreate(false);
      setForm({ name: "", description: "", clone_from: "" });
    } catch (e) {
      toast.error("Failed to create scenario");
    } finally { setSaving(false); }
  };

  const openDuplicateDialog = () => {
    if (!active) return;
    setDupForm({ name: `${active.name} (copy)`, description: active.description || "" });
    setOpenDup(true);
  };

  const submitDuplicate = async () => {
    if (!active) return;
    const trimmed = dupForm.name.trim();
    if (!trimmed) { toast.error("Please provide a name"); return; }
    setSaving(true);
    try {
      const res = await createScenario({
        name: trimmed,
        description: dupForm.description.trim(),
        clone_from: active.id,
      });
      toast.success(`Cloned as "${res.name}"`);
      onChange();
      onSelect(res.id);
      setOpenDup(false);
    } catch { toast.error("Duplicate failed"); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!active) return;
    try {
      await resetScenario(active.id);
      toast.success("Scenario reset to baseline values");
      onChange();
    } catch { toast.error("Reset failed"); }
  };

  const handleDelete = async () => {
    if (!active) return;
    try {
      // Switch active id BEFORE the delete + refetch to avoid a 404 refetch
      const baseline = scenarios.find(s => s.is_baseline);
      const fallback = baseline ? baseline.id : scenarios.find(s => s.id !== active.id)?.id;
      if (fallback) onSelect(fallback);
      await deleteScenario(active.id);
      toast.success("Scenario deleted");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-4 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 shrink-0">
        <Layers className="h-4 w-4 text-primary" />
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Active Scenario</span>
      </div>

      <div className="flex-1 flex items-center gap-3 flex-wrap">
        <Select value={activeId || ""} onValueChange={onSelect}>
          <SelectTrigger className="w-full sm:w-[320px]" data-testid="scenario-select">
            <SelectValue placeholder="Choose scenario" />
          </SelectTrigger>
          <SelectContent>
            {scenarios.map(s => (
              <SelectItem key={s.id} value={s.id} data-testid={`scenario-option-${s.id}`}>
                <div className="flex items-center gap-2">
                  {s.is_baseline && <Badge variant="secondary" className="text-[10px]">BASELINE</Badge>}
                  <span>{s.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {active && !active.is_baseline && (
          <Button
            variant="ghost"
            size="sm"
            onClick={openRenameDialog}
            className="h-9 px-2 text-muted-foreground hover:text-primary"
            title="Rename scenario"
            data-testid="btn-rename-scenario"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}

        {active && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-xs text-muted-foreground">
              Grand Total: <span className="num font-semibold text-foreground" data-testid="header-grand-total">{fmtMoney(active.totals.grand_total)}</span>
            </div>
            {active.totals.variance !== 0 && (
              <Badge
                variant="outline"
                className={active.totals.variance < 0 ? "border-emerald-500/50 text-emerald-700 bg-emerald-50" : "border-red-500/50 text-red-700 bg-red-50"}
                data-testid="header-variance"
              >
                {active.totals.variance < 0 ? "-" : "+"}{fmtMoney(Math.abs(active.totals.variance))} ({active.totals.variance_pct.toFixed(2)}%)
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="btn-new-scenario"><Plus className="h-4 w-4 mr-1" /> New</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Scenario</DialogTitle>
              <DialogDescription>Create an empty scenario or clone the active one.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="s-name">Name</Label>
                <Input id="s-name" data-testid="input-scenario-name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Value Engineering v1" />
              </div>
              <div>
                <Label htmlFor="s-desc">Description</Label>
                <Textarea id="s-desc" data-testid="input-scenario-desc" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Notes about this scenario" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => handleCreate(false)} disabled={saving} data-testid="btn-create-empty">Create Empty</Button>
              <Button onClick={() => handleCreate(true)} disabled={saving} data-testid="btn-create-clone">Clone from Active</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Button size="sm" variant="outline" onClick={openDuplicateDialog} data-testid="btn-clone">
          <Copy className="h-4 w-4 mr-1" /> Duplicate
        </Button>

        <Dialog open={openDup} onOpenChange={setOpenDup}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Duplicate scenario</DialogTitle>
              <DialogDescription>Give the copy a clear name so you can find it later.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="dup-name">Name</Label>
                <Input
                  id="dup-name"
                  data-testid="input-duplicate-name"
                  value={dupForm.name}
                  onChange={e => setDupForm(f => ({ ...f, name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="dup-desc">Description</Label>
                <Textarea
                  id="dup-desc"
                  data-testid="input-duplicate-desc"
                  value={dupForm.description}
                  onChange={e => setDupForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Notes about this copy"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenDup(false)}>Cancel</Button>
              <Button onClick={submitDuplicate} disabled={saving} data-testid="btn-confirm-duplicate">
                {saving ? "Creating…" : "Create duplicate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              disabled={aiRunning}
              className="bg-primary/90 hover:bg-primary text-primary-foreground gap-1"
              data-testid="btn-ai-pricing"
            >
              {aiRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              {aiRunning ? "Building…" : "Market Pricing"}
              <ChevronDown className="h-3 w-3 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>Create scenario using market rates</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => runAiPricingScenario("typical")}
              disabled={aiRunning}
              data-testid="btn-ai-pricing-typical"
              className="cursor-pointer"
            >
              <Sparkles className="h-3.5 w-3.5 mr-2 text-primary" />
              <div className="flex-1">
                <div className="text-sm font-semibold">Typical market rate</div>
                <div className="text-[11px] text-muted-foreground">Recommended · realistic target</div>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => runAiPricingScenario("low")}
              disabled={aiRunning}
              data-testid="btn-ai-pricing-low"
              className="cursor-pointer"
            >
              <Sparkles className="h-3.5 w-3.5 mr-2 text-emerald-600" />
              <div className="flex-1">
                <div className="text-sm font-semibold">Aggressive rate</div>
                <div className="text-[11px] text-muted-foreground">Best-case negotiation floor</div>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {active && !active.is_baseline && (
          <>
            <Button size="sm" variant="outline" onClick={handleReset} data-testid="btn-reset">
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive" data-testid="btn-delete">
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete "{active.name}"?</AlertDialogTitle>
                  <AlertDialogDescription>This will permanently remove this scenario's edits.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} data-testid="btn-confirm-delete">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>

      {/* Rename dialog */}
      <Dialog open={openRename} onOpenChange={setOpenRename}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename scenario</DialogTitle>
            <DialogDescription>Change the display name and description for this scenario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="rename-name">Name</Label>
              <Input
                id="rename-name"
                value={renameForm.name}
                onChange={e => setRenameForm(f => ({ ...f, name: e.target.value }))}
                onKeyDown={e => { if (e.key === "Enter") submitRename(); }}
                autoFocus
                data-testid="input-rename-name"
              />
            </div>
            <div>
              <Label htmlFor="rename-desc">Description (optional)</Label>
              <Textarea
                id="rename-desc"
                value={renameForm.description}
                onChange={e => setRenameForm(f => ({ ...f, description: e.target.value }))}
                data-testid="input-rename-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRename(false)}>Cancel</Button>
            <Button onClick={submitRename} disabled={saving} data-testid="btn-submit-rename">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
